import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled, { css } from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import {
	Post,
	SharedApplication,
	SharedArticle,
	SharedCarousel,
	SharedCourse,
	SharedGroup,
	SharedLive,
	SharedOrganization,
	SharedPost,
	SharedQuiz,
	SharedQuizResult,
} from "@domain/post/post";
import { useAsync } from "@utils/hooks";
import { SlidingModal } from "@ui/modals/slidingModal";
import { gap } from "@ui/styles/utils";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { SecondaryButton } from "@ui/components/buttons/secondaryButton";
import { Clamp } from "@ui/components/containers/clamp";
import { ChevronDownIcon, LockIcon } from "@ui/components/icons";
import { RichTextInput } from "@ui/components/inputs/draft/richTextInput";
import { row, Stack } from "@ui/components/layout";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { PostTargetSelection } from "@ui/components/post/target/postTargetSelection";
import { PostReference } from "./postReference";

interface EditReferencePostModalProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	initialPost?: Post;
	shared:
		| SharedPost
		| SharedCourse
		| SharedQuiz
		| SharedQuizResult
		| SharedOrganization
		| SharedGroup
		| SharedArticle
		| SharedCarousel
		| SharedLive
		| SharedApplication;
}

export const EditReferencePostModal: React.FC<EditReferencePostModalProps> = ({
	close,
	initialPost,
	shared,
	...htmlProps
}) => {
	const [shareMessage, setShareMessage] = useState(initialPost ? initialPost.text : ContentState.createFromText(""));
	const {
		courseService,
		organizationService,
		postService,
		quizService,
		groupService,
		articleService,
		carouselService,
		trackingService,
	} = useServices();
	const currentProfile = useCurrentProfile();
	const history = useHistory();
	const clearedShareMessage = shareMessage.getPlainText() ? shareMessage : undefined;
	const groupInReference = shared.reference && "group" in shared.reference ? (shared.reference.group ?? null) : null;
	const [postTarget, setPostTarget] = useState<{ id: string; name: string } | null>(
		initialPost?.group ?? groupInReference
	);
	const [editTargetMode, setEditTargetMode] = useState(false);

	const { run, loading } = useAsync(async () => {
		if (initialPost) {
			await postService.updatePost(initialPost.id, { ...initialPost, text: shareMessage });
		} else {
			if (!shared.reference) {
				return;
			}
			switch (shared.type) {
				case "SHARE_POST":
					await postService.sharePost(shared.reference.id, clearedShareMessage, postTarget?.id);
					setTimeout(() => {
						/** This hack fixes the share count not beign synced backend side */
						if (shared.reference?.id) {
							postService.postStore.fetch(shared.reference.id);
						}
					}, 5000);
					break;
				case "SHARE_COURSE":
					await courseService.shareCourse(shared.reference.id, clearedShareMessage, postTarget?.id);
					break;
				case "SHARE_ARTICLE":
					await articleService.shareArticle(shared.reference.id, clearedShareMessage, postTarget?.id);
					break;
				case "SHARE_QUIZ":
					await quizService.shareQuiz(shared.reference.id, clearedShareMessage, postTarget?.id);
					break;
				case "SHARE_QUIZ_RESULTS":
					await quizService.shareQuizResult(shared.reference.id, clearedShareMessage, postTarget?.id);
					break;
				case "SHARE_ORGANIZATION":
					await organizationService.shareOrganization(shared.reference.id, clearedShareMessage, postTarget?.id);
					break;
				case "SHARE_GROUP":
					await groupService.shareGroup(shared.reference.id, clearedShareMessage, postTarget?.id);
					break;
				case "SHARE_CAROUSEL":
					await carouselService.shareCarousel(shared.reference.id, clearedShareMessage, postTarget?.id);
					break;
				case "SHARE_LIVE_COURSE":
				case "SHARE_APPLICATION":
					// TODO : Implement share live course and application
					break;
			}

			postService.fetchFeed(postTarget?.id, postTarget ? "group" : "user");
			history.push(postTarget ? Path.Discussion(postTarget) : Path.Root);
		}
		trackingService.track("Feed - re-share post", {
			user: currentProfile?.primaryName,
			contentType: shared.type,
		});
		close();
	}, [shared.reference, shareMessage, clearedShareMessage, initialPost, postTarget?.id]);

	return (
		<Container
			currentIndex={+editTargetMode}
			close={close}
			title={I18n.t("post.share.modal.title")}
			sharedTitle
			{...htmlProps}
			elements={[
				<React.Fragment key="edition">
					<Stack gap={10}>
						<MessageForm>
							<ProfilePicture size={40} picture={currentProfile?.avatar} />
							<StyledInput
								mentionsEnabled
								richStyleEnabled={false}
								placeholder={I18n.t("post.input.placeholder")}
								value={shareMessage}
								onChange={(v) => setShareMessage(v)}
							/>
						</MessageForm>
						<PostTarget onClick={() => setEditTargetMode(true)} disabled={!!initialPost || !!groupInReference}>
							<Clamp size={1}>{postTarget?.name ?? I18n.t("post.target.default")}</Clamp>
							{initialPost || !!groupInReference ? <LockIcon /> : <ChevronDownIcon />}
						</PostTarget>
					</Stack>
					<ReadonlyPostReference post={shared} />
					<Buttons>
						<SecondaryButton onClick={close}>{I18n.t("cancel")}</SecondaryButton>
						<LoadingButton loading={loading} onClick={run}>
							{I18n.t("publish")}
						</LoadingButton>
					</Buttons>
				</React.Fragment>,
				<PostTargetSelection
					key="target"
					onTargetChosen={(group) => {
						setPostTarget(group);
						setEditTargetMode(false);
					}}
					cancel={() => setEditTargetMode(false)}
				/>,
			]}
		/>
	);
};

const Container = styled(SlidingModal)`
	${gap(20)};
`;

const MessageForm = styled.form`
	align-self: stretch;
	display: flex;
	align-items: center;
	${gap(12, "row")};

	> textarea {
		flex: 1;
	}
`;

const Buttons = styled.div`
	align-self: center;
	display: flex;
	align-items: center;
	${gap(24, "row")};
`;

const StyledInput = styled(RichTextInput)`
	border: 1px solid ${({ theme }) => theme.colors.lightergray};
	border-radius: 16px;
	flex: 1;

	.public-DraftEditor-content,
	.public-DraftEditorPlaceholder-root {
		padding: 10px 12px 9px;
		font-size: 0.8125rem;
	}
	.public-DraftEditorPlaceholder-root {
		font-weight: 400;
		opacity: 0.8;
	}
`;

const ReadonlyPostReference = styled(PostReference)`
	pointer-events: none;
`;

const PostTarget = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>`
	${({ disabled }) =>
		disabled &&
		css`
			pointer-events: none;
		`}
	${row(6, "flex-start", "center")};
	cursor: pointer;
	border-radius: 7px;
	padding: 5px 10px;
	background-color: ${({ theme }) => theme.colors.bodyBackground};
	${({ theme }) => theme.textStyles.label};
	> svg {
		flex-shrink: 0;
	}
`;
